// Články blogu. Čísla pocházejí z oborové statistiky FinPilot (okno 2023–2025, růst 2018–2024 a 2022–2025).
// Tělo je Markdown (GFM tabulky). Při aktualizaci statistiky čísla přepsat a změnit `updated`.

const POSTS = [
  {
    slug: "mzdy-rostou-rychleji-nez-trzby-firem",
    title: "Mzdy rostou dvakrát rychleji než tržby firem. Co ukazují data ČSÚ a účetní závěrky",
    description:
      "Podle ČSÚ rostla průměrná mzda v roce 2025 o 7,2 % a ve 2. čtvrtletí 2026 o 6,4 %. Účetní závěrky ukazují, že typické firmě rostly osobní náklady o 6–7 % ročně, tržby jen o 2–3 %. Kde to bolí nejvíc.",
    date: "2026-10-25",
    updated: "2026-10-25",
    tags: ["mzdy", "ČSÚ", "osobní náklady", "marže"],
    body: `
**Průměrná mzda v Česku podle ČSÚ vzrostla v roce 2025 nominálně o 7,2 % a ve 2. čtvrtletí 2026 o dalších 6,4 % na 51 966 Kč. Účetní závěrky stejných firem přitom ukazují, že tržby typické firmy rostly jen o 2–3 % ročně. Dvě třetiny firem měly v letech 2022–2024 osobní náklady rostoucí rychleji než tržby.**

## Co říká statistika mezd

Český statistický úřad měří průměrnou hrubou mzdu na přepočtený počet zaměstnanců:

| Období | Průměrná mzda | Nominální růst | Reálný růst | Inflace |
|---|---|---|---|---|
| Rok 2025 | – | 7,2 % | 4,6 % | 2,5 % |
| 1. čtvrtletí 2026 | 50 282 Kč | 8,1 % | 6,4 % | 1,6 % |
| 2. čtvrtletí 2026 | 51 966 Kč | 6,4 % | 4,3 % | 2,0 % |

Pro zaměstnance je to dobrá zpráva: mzdy po letech inflace znovu reálně rostou. Pro firmy to znamená, že největší nákladová položka většiny služeb a výroby roste dvakrát až třikrát rychleji než ceny.

Mezi odvětvími jsou velké rozdíly. V 1. čtvrtletí 2026 rostly mzdy nejrychleji v činnostech v oblasti nemovitostí (+22,1 %), v administrativních a podpůrných činnostech (+18,8 %) a ve stavebnictví (+12,6 %), nejpomaleji v energetice (+2,1 %). Ve 2. čtvrtletí vedly opět administrativní a podpůrné činnosti (+16,6 %), kam patří hlavně agentury práce a bezpečnostní služby.

## Co ukazují účetní závěrky

Mzdová statistika neříká, jestli si firmy vyšší mzdy mohou dovolit. To ukazuje srovnání osobních nákladů s tržbami ve výkazech. Vzali jsme firmy, které zveřejnily závěrky za oba srovnávané roky, aby výsledek nezkreslovaly firmy, které přibyly nebo zmizely.

| Stejné firmy | Firem | Osobní náklady, typická firma za rok | Tržby, typická firma za rok | Firmy, kterým náklady rostly rychleji |
|---|---|---|---|---|
| 2022 → 2024 | 13 799 | +6,8 % | +2,4 % | 67 % |
| 2024 → 2025 | 7 250 | +5,7 % | +2,6 % | 62 % |

Rozdíl se propisuje do ziskovosti. Osobní náklady spotřebovaly u typické firmy v roce 2025 už 69 % přidané hodnoty (rok předtím 67 %) a medián EBITDA marže stejných firem klesl z 8,0 % na 7,4 %.

[[graf:obory]]

## Kde je tlak největší

| Sekce CZ-NACE (stejné firmy 2022 → 2024) | Osobní náklady za rok | Tržby za rok | Firmy s rychlejším růstem nákladů |
|---|---|---|---|
| [Zpracovatelský průmysl](/oborove-statistiky/zpracovatelsky-prumysl) | +5,9 % | −0,1 % | 73 % |
| [Doprava a skladování](/oborove-statistiky/doprava-a-skladovani) | +6,9 % | +1,5 % | 75 % |
| [Velkoobchod a maloobchod](/oborove-statistiky/velkoobchod-a-maloobchod-opravy-motorovych-vozidel) | +6,3 % | +0,8 % | 70 % |
| [Zemědělství](/oborove-statistiky/zemedelstvi-lesnictvi-rybarstvi) | +4,8 % | −0,6 % | 71 % |
| [Stavebnictví](/oborove-statistiky/stavebnictvi) | +7,0 % | +1,3 % | 67 % |
| [Informační a komunikační činnosti](/oborove-statistiky/informacni-a-komunikacni-cinnosti) | +7,9 % | +8,1 % | 52 % |
| [Administrativní a podpůrné činnosti](/oborove-statistiky/administrativni-a-podpurne-cinnosti) | +8,6 % | +7,6 % | 51 % |
| [Ubytování a pohostinství](/oborove-statistiky/ubytovani-stravovani-a-pohostinstvi) | +13,3 % | +14,1 % | 51 % |

Obory se dělí na dvě skupiny. Služby s vysokým podílem práce (IT, administrativní služby, pohostinství) zdražení práce převedly do cen a tržby jim rostly zhruba stejně rychle jako náklady. Průmysl, doprava a obchod to nedokázaly: tržby stagnovaly, mzdy rostly a marže se zúžila.

Nejcitlivější je zpracovatelský průmysl: osobní náklady v něm v roce 2025 tvořily 73,5 % přidané hodnoty, v roce 2023 to bylo 68,4 %. Z přidané hodnoty tak zbývá zhruba čtvrtina, ze které se platí odpisy, úroky, daně a zisk.

## Co z toho plyne pro majitele a vedení firem

- **Rok 2027 tlak neuvolní.** Mzdy podle ČSÚ dál rostou o 6–8 % ročně a minimální mzda se od 1. ledna 2027 zvyšuje o 11,2 % na 24 900 Kč.
- **Rozhoduje produktivita, ne výše mzdy.** Sledujte podíl osobních nákladů na přidané hodnotě. Když roste dva roky po sobě, firma platí víc za stejný výkon.
- **Ceny.** Obory, které dokázaly zdražit, si marže udržely. Pokud vaše ceny rostou pomaleji než mzdy, marže se zužuje.
- **Srovnání s oborem.** Podíl osobních nákladů se mezi obory liší několikanásobně: v obchodě 8 % tržeb, v IT 29 %, ve zdravotní péči 49 %. Smysl má jen srovnání s vlastním oborem.

Podíl osobních nákladů a přidanou hodnotu vaší firmy proti oboru spočítá [finanční analýza](/financni-analyza), typické hodnoty pro každý obor jsou v [přehledu oborů](/obory).

## Zdroje

- [ČSÚ: Průměrné mzdy – 1. čtvrtletí 2026](https://csu.gov.cz/rychle-informace/prumerne-mzdy-1-ctvrtleti-2026)
- [ČSÚ: Průměrné mzdy – 4. čtvrtletí 2025](https://csu.gov.cz/rychle-informace/prumerne-mzdy-4-ctvrtleti-2025) (údaje za celý rok 2025)
- [Finance.cz: Průměrná mzda ve 2. čtvrtletí 2026](https://www.finance.cz/aktuality/prumerna-mzda-se-blizi-52-tisicum-v-jednom-odvetvi-mzdy-vzrostly-az-o-16-6/) (údaje ČSÚ ze 3. 9. 2026)
- [MPSV: Minimální mzda v roce 2027 vzroste na 24 900 korun](https://mpsv.gov.cz/minimalni-mzda-v-roce-2027-vzroste-na-24-900-korun)
`,
    charts: {
      obory: { typ: "bar", titulek: "Růst osobních nákladů a tržeb stejných firem za rok, 2022–2024 (%)", jednotka: "%",
               popisky: ["Zpracovatelský průmysl", "Doprava a skladování", "Obchod", "Zemědělství", "Stavebnictví", "IT a komunikace", "Administrativní služby", "Ubytování a pohostinství"],
               rady: [{ nazev: "Osobní náklady", hodnoty: [5.9, 6.9, 6.3, 4.8, 7.0, 7.9, 8.6, 13.3] },
                      { nazev: "Tržby", hodnoty: [-0.1, 1.5, 0.8, -0.6, 1.3, 8.1, 7.6, 14.1] }] },
    },
    method: "Mzdové údaje jsou z ČSÚ (průměrná hrubá měsíční mzda na přepočtené počty zaměstnanců). Údaje o firmách jsou z účetních závěrek firem s obratem nad 30 milionů korun: osobní náklady (mzdy a odvody) a tržby stejných firem v obou letech, medián ročního růstu. Osobní náklady firmy rostou i se změnou počtu zaměstnanců, proto nejsou přímo srovnatelné s průměrnou mzdou ČSÚ. Rok 2025 zatím pokrývá zhruba polovinu firem.",
    faq: [
      ["O kolik vzrostla průměrná mzda v roce 2025?", "Podle ČSÚ nominálně o 7,2 % a reálně o 4,6 % při inflaci 2,5 %. Ve 2. čtvrtletí 2026 činila průměrná mzda 51 966 Kč, meziročně o 6,4 % více."],
      ["Rostou firmám náklady na zaměstnance rychleji než tržby?", "Ano. U stejných firem rostly v letech 2022–2024 osobní náklady typické firmy o 6,8 % ročně a tržby o 2,4 %. Rychlejší růst osobních nákladů než tržeb mělo 67 % firem."],
      ["Ve kterých oborech je tlak mezd na marže největší?", "Ve zpracovatelském průmyslu, dopravě a obchodě: osobní náklady rostly o 6–7 % ročně, tržby stagnovaly. IT, administrativní služby a pohostinství zdražení práce převedly do cen."],
    ],
  },
  {
    slug: "eet-2-0-od-roku-2027-co-ukazuji-cisla-oboru",
    title: "EET 2.0 od roku 2027: co čísla říkají o restauracích, hotelech a obchodech",
    description:
      "EET 2.0 startuje v lednu 2027. Restaurace s obratem nad 30 milionů mají EBITDA marži 7,2 %, hotely 12,7 % a ve ztrátě je pětina z nich. Co nová evidence změní a co ukázala první EET v letech 2016–2019.",
    date: "2026-10-25",
    updated: "2026-10-25",
    tags: ["EET", "pohostinství", "maloobchod", "daně"],
    body: `
**Elektronická evidence tržeb se vrací: zákon o EET 2.0 podepsal prezident v září 2026, pilotní provoz začne 1. ledna 2027 a plný provoz 1. února 2027. Nejvíc se dotkne pohostinství, ubytování a maloobchodu – oborů, kde typická firma s obratem nad 30 milionů korun vydělá na provozní úrovni 4,5 až 12,7 % tržeb a pětina restaurací a hotelů končí ve ztrátě.**

## Co EET 2.0 přináší

- **Kdo eviduje:** podnikatelé s příjmy z hotovostních plateb a plateb kartou na místě, tedy obchody, provozovny, služby.
- **Které platby:** hotovost a karta „na místě“. Platby přes platební brány e-shopů a QR kódy na e-shopech se neevidují.
- **Jaká data:** jen základní údaje – kdo, kdy, kde a jaká celková částka. Bez položek a bez rozpisu DPH.
- **Účtenky:** zákon tisk účtenky z EET nevyžaduje a odpadá i povinná informační cedule.
- **Výjimka:** drobní podnikatelé v prvním pásmu paušální daně s příjmy do 1 milionu korun ročně si mohou vybrat režim bez evidence za vyšší měsíční paušální platbu.
- **Termíny:** od 1. 11. 2026 funkce v Daňové informační schránce, od 1. 12. 2026 bezplatná aplikace MOJE EET, od 1. 1. 2027 pilotní provoz, od 1. 2. 2027 plný provoz.
- **Spolu s EET** byla schválena snížená 12% sazba DPH na nealkoholické nápoje podávané v rámci stravovacích služeb.

Vláda od evidence očekává přes 14 miliard korun ročně navíc na daních.

## Jak vydělávají obory, kterých se EET týká nejvíc

Mediány firem s obratem nad 30 milionů korun za roky 2023–2025:

| Obor (CZ-NACE) | Firem | EBITDA marže | Čistá marže (ROS) | Podíl osobních nákladů | Ve ztrátě | Záporný vlastní kapitál |
|---|---|---|---|---|---|---|
| {{56|Stravování a pohostinství}} | 231 | 7,2 % | 3,6 % | 22,6 % | 19,6 % | 11,6 % |
| {{55|Ubytování}} | 166 | 12,7 % | 6,2 % | 23,2 % | 21,5 % | 17,4 % |
| {{47|Maloobchod}} | 1 266 | 4,5 % | 2,6 % | 9,2 % | 15,2 % | 4,7 % |
| {{45|Prodej a opravy aut}} | 830 | 4,7 % | 2,1 % | 7,6 % | 13,4 % | 2,8 % |

Pohostinství je obor s tenkou marží a vysokým podílem práce: zhruba čtvrtina restaurací má EBITDA marži pod 3 %. Hotely vydělávají víc, ale jsou silně zadlužené – záporný vlastní kapitál má každý šestý. To je důležité pro debatu o EET: obor, který bojuje o každé procento marže, citlivě vnímá jakýkoli náklad navíc i rozdíly mezi poctivými a nepoctivými konkurenty.

## Co ukázala první EET

První EET platila pro restaurace a hotely od prosince 2016, pro obchod od března 2017 a v roce 2020 byla přerušena. Firmy s obratem nad 30 milionů v tom období vykázaly:

[[graf:historie]]

| Rok | Stravování: růst tržeb | Stravování: EBITDA marže | Ubytování: růst tržeb | Maloobchod: růst tržeb |
|---|---|---|---|---|
| 2016 | – | 7,0 % | 3,3 % | 15,0 % |
| 2017 | 18,8 % | 8,0 % | 10,2 % | 9,6 % |
| 2018 | 9,8 % | 7,1 % | 3,1 % | 7,9 % |
| 2019 | 5,6 % | 6,4 % | 4,0 % | 4,8 % |

V roce 2017 vykázaly restaurace i hotely výrazný skok tržeb a marže restaurací vzrostla o procentní bod. Opatrně: rok 2017 byl zároveň rokem silného růstu ekonomiky a spotřeby, takže čísla samotný vliv EET neprokazují. Už v roce 2018 se růst vrátil k obvyklým hodnotám a marže restaurací v roce 2019 klesla pod úroveň roku 2016; zároveň stoupl podíl osobních nákladů z 21,5 % na 23,1 % tržeb.

## Náš pohled

- **Pro velké provozy je EET 2.0 hlavně administrativní změna.** Firmy s obratem v desítkách milionů většinou už mají pokladní systémy, které tržby evidují. Systém je navržený tak, aby u mnoha pokladen stačila aktualizace softwaru.
- **Hlavní efekt je konkurenční.** Pokud evidence sníží podíl nepřiznaných tržeb u menších konkurentů, poctivé provozy přestanou soutěžit s firmami, které neplatí celé daně. U oboru s 3% čistou marží jde o podstatnou výhodu.
- **Snížené DPH na nealko pomůže marži.** Když restaurace ponechá ceny nealkoholických nápojů, zvýší se jí čistý výnos z každého prodaného nápoje zhruba o 8 % (poměr 1,21 / 1,12). Na celkové marži se to projeví podle podílu nealka na tržbách.
- **Rizikem jsou nejslabší provozy.** Pětina restaurací a hotelů je ve ztrátě a 12–17 % má záporný vlastní kapitál. Pro ty z nich, které dnes část tržeb nepřiznávají, může evidence znamenat konec.

Jak si v tržbách, marži a nákladech na personál stojí vaše provozovna proti oboru, spočítá [finanční analýza](/financni-analyza) z účetní závěrky.

## Zdroje

- [Právní prostor: Prezident podepsal zákon o EET (18. 9. 2026)](https://pravniprostor.cz/aktuality/prezident-vetoval-zakon-o-statnich-zamestnancich-podepsal-eet-a-stavebni-zakon)
- [epravo.cz: EET 2.0 – nová podoba evidence tržeb](https://www.epravo.cz/top/clanky/eet-20-poslanecka-snemovna-schvalila-novou-podobu-evidence-trzeb-121668.html) (rozsah, výjimky, termíny)
`,
    charts: {
      historie: { typ: "line", titulek: "EBITDA marže restaurací a hotelů s obratem nad 30 mil. Kč (%)", jednotka: "%",
                  popisky: ["2016", "2017", "2018", "2019", "2020", "2021", "2022", "2023", "2024", "2025"],
                  rady: [{ nazev: "Stravování a pohostinství", hodnoty: [7.0, 8.0, 7.1, 6.4, 4.0, 6.7, 8.1, 7.7, 7.1, 7.2] },
                         { nazev: "Ubytování", hodnoty: [12.1, 14.8, 9.2, 12.6, -3.0, 9.8, 12.9, 14.4, 11.2, 11.9] }] },
    },
    method: "Údaje o oborech jsou mediány firem s obratem nad 30 milionů korun z jejich účetních závěrek; souhrnné hodnoty jsou za roky 2023–2025, historie po jednotlivých letech. EBITDA marže = EBITDA / tržby, ROS = výsledek před zdaněním / tržby. Menší provozovny, kterých se EET týká nejvíc, ve statistice nejsou. Informace o zákoně pocházejí z uvedených zdrojů k říjnu 2026; podrobnosti ověřte ve Sbírce zákonů a u Finanční správy.",
    faq: [
      ["Od kdy platí EET 2.0?", "Pilotní provoz začíná 1. ledna 2027, plný provoz 1. února 2027. Prezident zákon podepsal v září 2026."],
      ["Musí se v EET 2.0 evidovat platby kartou?", "Ano, evidují se hotovostní platby a platby kartou na místě. Platby přes platební brány e-shopů a QR kódy na e-shopech se neevidují."],
      ["Jakou marži mají restaurace v Česku?", "Typická restaurace s obratem nad 30 milionů korun má EBITDA marži 7,2 % a čistou marži před zdaněním 3,6 % (medián 2023–2025). Ve ztrátě je 19,6 % firem."],
    ],
  },
  {
    slug: "bankroty-firem-2026-varovne-signaly-v-zaverkach",
    title: "Bankrotů firem přibývá. Pět varovných signálů v účetní závěrce a obory s nejvyšším rizikem",
    description:
      "Od ledna do srpna 2026 zbankrotovalo 538 firem, návrhů na bankrot přibylo o pětinu. Účetní závěrky větších firem ukazují, kde se riziko hromadí: hotely, restaurace, nemovitosti a automotive. Jak poznat rizikového odběratele.",
    date: "2026-10-25",
    updated: "2026-10-25",
    tags: ["bankroty", "insolvence", "riziko", "odběratelé"],
    body: `
**Od ledna do srpna 2026 soudy vyhlásily bankrot 538 obchodních společností, o 4 % víc než loni, a návrhů na bankrot přibylo o pětinu. Účetní závěrky firem s obratem nad 30 milionů korun ukazují, že varovné signály se hromadí v ubytování, pohostinství, nemovitostech a výrobě aut. Stavebnictví a velkoobchod jsou mezi většími firmami stabilní – tam krachují hlavně malé firmy.**

## Co říkají čísla o bankrotech

- **2026:** podle CRIF bylo od ledna do srpna vyhlášeno 538 bankrotů firem (+4 %) a soudy přijaly 895 návrhů (+20 %). Za osm měsíců je to nejvíc za posledních sedm let.
- **2025:** CRIF napočítal 751 bankrotů firem (+9 %), Dun & Bradstreet 746 (+10 %). Podle D&B krachují nejčastěji menší firmy s tržbami do 30 milionů a třetina bankrotů připadá na pronájem a správu nemovitostí, výstavbu budov, stravování a velkoobchod.

Bankrot je ale až poslední krok. Firmy, které se k němu blíží, jsou ve výkazech vidět roky předem.

## Pět varovných signálů v účetní závěrce

1. **Záporný vlastní kapitál.** Ztráty firmy převýšily vše, co do ní majitelé vložili. Ve firmách s obratem nad 30 milionů jde o 3,9 % firem.
2. **Dvě ztráty po sobě.** Jedna ztráta může být investice nebo výkyv, dvě za sebou jsou trend. Týká se 7,8 % firem.
3. **Běžná likvidita pod 1.** Krátkodobé závazky jsou vyšší než oběžný majetek: firma platí dodavatelům z peněz, které teprve přijdou. Týká se 13,8 % firem.
4. **Prodlužující se splatnost závazků.** Když doba obratu závazků roste rychleji než u oboru, firma financuje provoz z peněz dodavatelů. Typická firma má závazky ve výši 61 dní tržeb.
5. **Pokles tržeb přes 10 %.** Samo o sobě nic neznamená, v kombinaci s předchozími body je to silný signál. Meziročně se týká 23 % firem.

[[graf:riziko]]

## Kde se riziko hromadí

Podíl firem s varovnými signály v letech 2023–2025, firmy s obratem nad 30 milionů korun:

| Obor (CZ-NACE) | Firem | Záporný vlastní kapitál | Ztráta | Dvě ztráty po sobě | Likvidita pod 1 |
|---|---|---|---|---|---|
| {{55|Ubytování}} | 166 | 17,4 % | 21,5 % | 13,3 % | 30,3 % |
| {{56|Stravování a pohostinství}} | 231 | 11,6 % | 19,6 % | 11,6 % | 28,6 % |
| {{68|Nemovitosti}} | 1 344 | 7,7 % | 25,3 % | 17,3 % | 25,4 % |
| {{29|Výroba motorových vozidel}} | 223 | 6,2 % | 23,4 % | 16,5 % | 26,9 % |
| {{49|Pozemní doprava}} | 920 | 4,7 % | 17,1 % | 9,5 % | 24,1 % |
| {{47|Maloobchod}} | 1 266 | 4,7 % | 15,2 % | 8,9 % | 12,6 % |
| **Všechny firmy** | 23 921 | 3,9 % | 14,3 % | 7,8 % | 13,8 % |
| {{46|Velkoobchod}} | 4 154 | 2,8 % | 11,4 % | 5,8 % | 6,8 % |
| {{43|Specializované stavební činnosti}} | 917 | 3,1 % | 10,1 % | 4,2 % | 9,6 % |
| {{41|Výstavba budov}} | 1 330 | 2,2 % | 7,9 % | 2,7 % | 9,5 % |

## Co z toho plyne

- **Stavebnictví a velkoobchod jsou mezi bankroty kvůli malým firmám.** Větší stavební firmy patří k nejstabilnějším vůbec: dvě ztráty po sobě má jen 2,7 % z nich. Riziko je v subdodavatelích a malých firmách, které ve statistice nad 30 milionů nejsou.
- **Pohostinství a hotely nesou riziko i u větších firem.** Každý šestý hotel s obratem nad 30 milionů má záporný vlastní kapitál a téměř třetina hotelů a restaurací má likviditu pod 1.
- **Nemovitosti potřebují jiný pohled.** Ztráty jsou tu často účetní (odpisy, úroky) a hodnotu drží majetek. Dvě ztráty po sobě u 17 % firem a likvidita pod 1 u čtvrtiny ale ukazují i skutečné finanční potíže části projektových firem.
- **Výroba aut a dílů patří mezi rizikové obory.** U výrobců a dodavatelů je ve ztrátě téměř čtvrtina firem a 16,5 % má ztrátu dva roky po sobě.

Před větší zakázkou nebo dodávkou na splatnost se vyplatí podívat do závěrky odběratele a porovnat ji s oborem. Typické hodnoty ukazatelů najdete v [přehledu oborů](/obory), rozbor celé závěrky udělá [finanční analýza](/financni-analyza).

## Zdroje

- [BusinessInfo.cz: Od ledna do srpna v Česku zbankrotovalo 538 firem](https://www.businessinfo.cz/clanky/od-ledna-do-srpna-v-cesku-zbankrotovalo-538-firem-mezirocne-jich-opet-pribylo/) (údaje CRIF)
- [BusinessInfo.cz: V roce 2025 zbankrotovalo rekordní množství firem](https://www.businessinfo.cz/clanky/v-roce-2025-zbankrotovalo-rekordni-mnozstvi-firem-podniky-hure-zvladaji-rizika/) (údaje Dun & Bradstreet)
`,
    charts: {
      riziko: { typ: "bar", titulek: "Firmy se záporným vlastním kapitálem a se dvěma ztrátami po sobě, 2023–2025 (%)", jednotka: "%",
                popisky: ["Ubytování", "Stravování", "Nemovitosti", "Výroba aut", "Pozemní doprava", "Maloobchod", "Všechny firmy", "Velkoobchod", "Výstavba budov"],
                rady: [{ nazev: "Záporný vlastní kapitál", hodnoty: [17.4, 11.6, 7.7, 6.2, 4.7, 4.7, 3.9, 2.8, 2.2] },
                       { nazev: "Dvě ztráty po sobě", hodnoty: [13.3, 11.6, 17.3, 16.5, 9.5, 8.9, 7.8, 5.8, 2.7] }] },
    },
    method: "Údaje o bankrotech pocházejí od CRIF a Dun & Bradstreet (viz zdroje). Varovné signály jsou spočítané z účetních závěrek firem s obratem nad 30 milionů korun za roky 2023–2025: podíl firmoroků se záporným vlastním kapitálem, se záporným výsledkem před zdaněním a s běžnou likviditou pod 1; „dvě ztráty po sobě“ = ztráta v letech 2023 i 2024 nebo 2024 i 2025. Statistika nezahrnuje malé firmy, kde je bankrotů nejvíc.",
    faq: [
      ["Kolik firem v Česku zbankrotovalo v roce 2026?", "Podle CRIF bylo od ledna do srpna 2026 vyhlášeno 538 bankrotů obchodních společností, o 4 % víc než ve stejném období roku 2025. Návrhů na bankrot bylo 895, o pětinu víc."],
      ["Ve kterých oborech firmy krachují nejčastěji?", "Podle Dun & Bradstreet připadá třetina bankrotů na pronájem a správu nemovitostí, výstavbu budov, stravování a velkoobchod. Mezi většími firmami jsou nejrizikovější ubytování, pohostinství, nemovitosti a výroba aut."],
      ["Jak poznat, že odběratel může zkrachovat?", "Ve výkazech hledejte záporný vlastní kapitál, dvě ztráty po sobě, běžnou likviditu pod 1, rostoucí dobu splatnosti závazků a pokles tržeb přes 10 %. Nejsilnější je kombinace několika signálů."],
    ],
  },
  {
    slug: "ktere-obory-rostou-nejrychleji",
    title: "Které obory v Česku rostou nejrychleji (2022–2025)",
    description:
      "Žebříček oborů podle růstu tržeb typické firmy: cestovní kanceláře 17 % ročně, ubytování 11 %, IT 8 %. Hutnictví a dřevozpracující průmysl klesají.",
    date: "2026-10-03",
    updated: "2026-10-03",
    tags: ["růst", "obory", "tržby"],
    charts: {
      top: { typ: "bar", titulek: "Růst tržeb typické firmy za rok, 2022–2025", jednotka: "%",
             popisky: ["79 Cestovní kanceláře", "55 Ubytování", "93 Sport a rekreace", "70 Poradenství v řízení", "86 Zdravotní péče",
                       "56 Stravování", "36 Voda", "82 Administrativní činnosti", "62 Informační technologie", "69 Právní a účetní"],
             rady: [{ nazev: "Růst tržeb", hodnoty: [17.4, 10.9, 9.8, 9.8, 8.6, 8.6, 8.5, 8.4, 8.2, 8.0] }] },
    },
    method: "Roční růst (CAGR) tržeb se počítá pro každou firmu zvlášť mezi prvním a posledním rokem okna; firma musí mít v obou letech kladné tržby. „Typická firma“ je medián těchto hodnot v oboru, „součet oboru“ je růst součtu tržeb stejných firem. Do žebříčků vstupují oddíly CZ-NACE s aspoň 30 firmami. Okna: 2018–2024 a 2022–2025.",
    faq: [
      ["Který obor v Česku roste nejrychleji?", "V letech 2022–2025 cestovní agentury a kanceláře: tržby typické firmy rostly o 17,4 % ročně. V delším období 2018–2024 vede vzdělávání (10,9 %), ostatní finanční činnosti (9,9 %) a zdravotní péče (9,6 %)."],
      ["Které obory klesají?", "V letech 2022–2025 klesaly tržby typické firmy v hutnictví a slévárenství (−5,1 % ročně), ve zpracování dřeva a lesnictví (−3,0 %) a ve výrobě papíru (−2,2 %)."],
      ["Jaký je rozdíl mezi růstem typické firmy a růstem oboru?", "Typická firma je medián růstu jednotlivých firem, každá má stejnou váhu. Růst oboru je součet tržeb všech firem, takže ho určují největší z nich. Pro srovnání vlastní firmy je vhodnější medián."],
    ],
    body: `
**Nejrychleji rostou tržby firmám v cestovním ruchu, ubytování a zdravotnictví. Typická cestovní kancelář zvyšovala tržby v letech 2022–2025 o 17,4 % ročně, typická firma v hutnictví o 5,1 % ročně ztrácela.** Čísla jsou mediány ročního růstu (CAGR) firem v oboru, tedy růst „typické firmy“, ne součet celého trhu.

## Deset nejrychleji rostoucích oborů (2022–2025)

[[graf:top]]

| Obor (CZ-NACE) | Typická firma za rok | Součet oboru za rok | Podíl rostoucích firem | Firem |
|---|---|---|---|---|
| {{79|Cestovní agentury a kanceláře}} | 17,4 % | 14,3 % | 93 % | 30 |
| {{55|Ubytování}} | 10,9 % | 13,0 % | 89 % | 47 |
| {{93|Sportovní, zábavní a rekreační činnosti}} | 9,8 % | 26,1 % | 95 % | 37 |
| {{70|Vedení podniků a poradenství v řízení}} | 9,8 % | −2,1 % | 80 % | 49 |
| {{86|Zdravotní péče}} | 8,6 % | 8,4 % | 93 % | 137 |
| {{56|Stravování a pohostinství}} | 8,6 % | 10,9 % | 77 % | 75 |
| {{36|Shromažďování, úprava a rozvod vody}} | 8,5 % | 10,3 % | 98 % | 49 |
| {{82|Administrativní a podpůrné činnosti}} | 8,4 % | 12,0 % | 71 % | 48 |
| {{62|Informační technologie}} | 8,2 % | 6,0 % | 76 % | 300 |
| {{69|Právní a účetnické činnosti}} | 8,0 % | 9,0 % | 82 % | 62 |

„Typická firma“ je medián ročního růstu jednotlivých firem. „Součet oboru“ je růst tržeb všech firem oboru dohromady, takže ho táhnou největší hráči.

První tři místa jsou z velké části návrat po covidu: rok 2022 byl pro cestovní ruch a ubytování ještě slabý, takže základna je nízká.

## Dlouhé okno 2018–2024: kdo roste trvale

Šestileté okno obsahuje covid i následné oživení a rok 2024 je v datech úplný. Pořadí se tu mění: cestovní ruch mizí a nahoře zůstávají obory, které rostou bez ohledu na cyklus.

| Obor (CZ-NACE) | Typická firma za rok | Součet oboru za rok | Podíl rostoucích firem | Firem |
|---|---|---|---|---|
| {{85|Vzdělávání}} | 10,9 % | 9,7 % | 91 % | 33 |
| {{66|Ostatní finanční činnosti}} | 9,9 % | 9,1 % | 80 % | 51 |
| {{86|Zdravotní péče}} | 9,6 % | 10,2 % | 97 % | 176 |
| {{62|Informační technologie}} | 9,2 % | 8,6 % | 89 % | 340 |
| {{74|Ostatní profesní, vědecké a technické činnosti}} | 9,1 % | 18,5 % | 81 % | 113 |
| {{70|Vedení podniků a poradenství v řízení}} | 8,8 % | −2,9 % | 76 % | 78 |
| {{80|Bezpečnostní a pátrací činnosti}} | 8,6 % | 8,1 % | 80 % | 66 |
| {{69|Právní a účetnické činnosti}} | 8,4 % | 6,0 % | 87 % | 84 |
| {{72|Výzkum a vývoj}} | 8,3 % | 8,7 % | 81 % | 41 |
| {{38|Sběr a zpracování odpadů}} | 7,7 % | 6,8 % | 90 % | 133 |

Na opačném konci dlouhého okna stojí {{64|finanční zprostředkování}} (1,1 % ročně), {{24|hutnictví a slévárenství}} (1,4 %), {{22|výroba plastů a pryže}} (2,0 %), {{31|výroba nábytku}} (2,0 %), {{13|výroba textilií}} (2,5 %) a {{28|výroba strojů a zařízení}} (2,6 %). Při průměrné inflaci v těchto letech to znamená reálný pokles.

## Kde tržby klesají (2022–2025)

| Obor (CZ-NACE) | Typická firma za rok | Součet oboru za rok | Podíl rostoucích firem |
|---|---|---|---|
| {{24|Výroba základních kovů, hutnictví, slévárenství}} | −5,1 % | −5,9 % | 27 % |
| {{16|Zpracování dřeva}} | −3,0 % | −3,8 % | 39 % |
| {{02|Lesnictví a těžba dřeva}} | −3,0 % | −4,9 % | 39 % |
| {{17|Výroba papíru}} | −2,2 % | −2,3 % | 42 % |
| {{22|Výroba pryžových a plastových výrobků}} | −1,1 % | +0,3 % | 46 % |
| {{52|Skladování a vedlejší činnosti v dopravě}} | −1,1 % | −1,0 % | 46 % |

Společný jmenovatel: energeticky náročná výroba a obory závislé na cenách komodit. Po cenovém vrcholu roku 2022 tu tržby klesají i firmám, které prodávají stejné objemy.

## Typická firma versus celý trh

Vedle mediánu počítáme i agregovaný růst: součet tržeb všech firem oboru na konci a na začátku období. Když se obě čísla rozcházejí, říká to něco o struktuře oboru.

- **Poradenství v řízení (70):** typická firma roste o 9,8 % ročně, součet oboru klesá o 2,1 %. Největší firma tvořila třetinu tržeb, takže součet oboru popisuje hlavně ji. V dlouhém okně je to stejné (+8,8 % proti −2,9 %).
- **Sport a rekreace (93):** typická firma +9,8 %, součet +26 %. Táhnou velcí hráči.
- **Informační technologie (62):** typická firma +8,2 %, součet +6,0 %. Menší firmy rostou rychleji než největší.
- **Ostatní profesní a technické činnosti (74), 2018–2024:** typická firma +9,1 %, součet +18,5 %. Jedna firma tu tvořila tři čtvrtiny tržeb oboru.

Platí jednoduché pravidlo: pro srovnání vlastní firmy se dívejte na medián, pro velikost a dynamiku trhu na agregát. V oborech, kde jedna firma tvoří desítky procent tržeb, agregát popisuje hlavně ji.

## Na co si dát pozor

Účetní závěrky za rok 2025 zatím zveřejnila zhruba polovina firem a častěji jsou to ty zdravější. Krátké okno 2022–2025 proto může růst mírně nadhodnocovat; dlouhé okno 2018–2024 je úplné.

Růst vlastní firmy si proti oboru můžete porovnat ve [finanční analýze](/financni-analyza), čísla za všechny obory jsou v [oborových statistikách](/oborove-statistiky).
`,
  },
  {
    slug: "kolik-firem-je-ve-ztrate",
    title: "Kolik českých firem je ve ztrátě? Podíl ztrátových firem podle oboru",
    description:
      "Ve ztrátě končí 14 % českých firem. V kultuře a rekreaci 37 %, v nemovitostech 25 %, ve stavebnictví jen 9 %. Přehled podle oborů a vývoj od roku 2019.",
    date: "2026-10-03",
    updated: "2026-10-03",
    tags: ["ztrátovost", "riziko", "obory"],
    charts: {
      vyvoj: { typ: "line", titulek: "Podíl ztrátových firem podle sekcí, 2019–2025", jednotka: "%",
               popisky: ["2019", "2020", "2021", "2022", "2023", "2024", "2025"],
               rady: [
                 { nazev: "Ubytování a pohostinství", hodnoty: [24.8, 60.5, 43.8, 24.9, 18.6, 22.6, 20.3] },
                 { nazev: "Kultura a rekreace", hodnoty: [39.2, 57.4, 47.5, 39.2, 31.6, 43.6, 35.7] },
                 { nazev: "Zemědělství", hodnoty: [12.0, 12.4, 12.0, 9.3, 18.0, 24.3, 15.0] },
                 { nazev: "Zpracovatelský průmysl", hodnoty: [16.8, 18.0, 15.0, 16.0, 14.5, 17.2, 15.9] },
                 { nazev: "Stavebnictví", hodnoty: [8.9, 9.2, 11.4, 11.4, 7.1, 10.1, 9.0] },
               ] },
    },
    method: "Ztrátová je firma se záporným výsledkem hospodaření před zdaněním v daném roce. Podíl se počítá z firmoroků: firma se třemi roky ve vzorku se započte třikrát. Souhrnné hodnoty jsou za roky 2023–2025, graf ukazuje jednotlivé roky. Oddíly v podrobné tabulce mají aspoň 50 firem.",
    faq: [
      ["Kolik procent českých firem je ve ztrátě?", "V letech 2023–2025 skončilo ve ztrátě 14,3 % českých firem s obratem nad 30 milionů korun."],
      ["Ve kterém oboru je nejvíc ztrátových firem?", "Mezi sekcemi v kulturních, zábavních a rekreačních činnostech (36,8 %), v nemovitostech (25,3 %) a v ubytování a pohostinství (20,4 %). Mezi oddíly ve sportovních a rekreačních činnostech (46,2 %)."],
      ["Jak covid ovlivnil ztrátovost firem?", "V roce 2020 bylo ve ztrátě 60,5 % firem v ubytování a pohostinství a 57,4 % v kultuře a rekreaci. Pohostinství se do roku 2023 vrátilo na 18,6 %."],
    ],
    body: `
**V letech 2023–2025 skončilo ve ztrátě 14,3 % českých firem s obratem nad 30 milionů korun – tedy zhruba každá sedmá.** Mezi obory jsou ale rozdíly desetinásobné: v těžbě je ve ztrátě 3,6 % firem, v kultuře a rekreaci 36,8 %.

Ztrátová firma je pro nás ta, která má záporný výsledek hospodaření před zdaněním. Počítáme podíl „firmoroků“, takže firma se třemi roky ve vzorku se započte třikrát.

## Podíl ztrátových firem podle sekcí

| Sekce CZ-NACE | Ztrátové firmy | Firem ve vzorku |
|---|---|---|
| R Kulturní, zábavní a rekreační činnosti | 36,8 % | 150 |
| L Činnosti v oblasti nemovitostí | 25,3 % | 1 344 |
| I Ubytování, stravování a pohostinství | 20,4 % | 397 |
| A Zemědělství, lesnictví a rybářství | 19,7 % | 917 |
| K Peněžnictví a pojišťovnictví | 17,5 % | 528 |
| H Doprava a skladování | 16,3 % | 1 295 |
| C Zpracovatelský průmysl | 15,8 % | 5 690 |
| **Všechny firmy** | **14,3 %** | **23 921** |
| Q Zdravotní a sociální péče | 13,3 % | 372 |
| G Velkoobchod a maloobchod | 12,5 % | 6 423 |
| D Výroba a rozvod energií | 12,4 % | 353 |
| N Administrativní a podpůrné činnosti | 12,4 % | 807 |
| M Profesní, vědecké a technické činnosti | 11,7 % | 1 517 |
| P Vzdělávání | 11,3 % | 67 |
| J Informační a komunikační činnosti | 11,0 % | 1 036 |
| E Voda, odpady a sanace | 9,1 % | 382 |
| S Ostatní činnosti | 8,9 % | 69 |
| F Stavebnictví | 8,6 % | 2 443 |
| B Těžba a dobývání | 3,6 % | 75 |

## Nejrizikovější a nejbezpečnější obory podrobněji

Na úrovni oddílů (obory s aspoň 50 firmami) jsou extrémy ještě výraznější.

| Nejvíc ztrátových firem | Podíl | Nejméně ztrátových firem | Podíl |
|---|---|---|---|
| {{93|Sportovní, zábavní a rekreační činnosti}} | 46,2 % | {{36|Úprava a rozvod vody}} | 1,1 % |
| {{24|Hutnictví a slévárenství}} | 28,9 % | {{08|Ostatní těžba a dobývání}} | 3,8 % |
| {{13|Výroba textilií}} | 26,1 % | {{42|Inženýrské stavitelství}} | 6,6 % |
| {{68|Nemovitosti}} | 25,3 % | {{41|Výstavba budov}} | 7,9 % |
| {{29|Výroba motorových vozidel}} | 23,4 % | {{71|Architektonické a inženýrské činnosti}} | 9,1 % |

U nemovitostí vysoká ztrátovost neznamená krizi: velká část firem jsou projektové společnosti, které vykazují účetní ztrátu kvůli odpisům a úrokům, a hodnotu tvoří růst ceny majetku. U hutnictví, textilu a automobilového průmyslu jde naopak o skutečný tlak na marže.

## Jak se ztrátovost měnila

[[graf:vyvoj]]

Covid se v číslech projevil jen u několika oborů, zato naplno. V ubytování a pohostinství bylo v roce 2020 ve ztrátě 60,5 % firem, v kultuře a rekreaci 57,4 %. Pohostinství se do roku 2023 vrátilo na 18,6 %, kultura a rekreace zůstává nad 30 %.

Zemědělství šlo opačným směrem: z 9,3 % v roce 2022 na 24,3 % v roce 2024, když po rekordních cenách komodit přišel pokles. Zpracovatelský průmysl a stavebnictví se drží v úzkém pásmu: průmysl mezi 14 a 18 %, stavebnictví mezi 7 a 11 %.

Rok 2025 berte s rezervou: závěrky zatím zveřejnila zhruba polovina firem a častěji ty zdravější, takže skutečný podíl bude spíš vyšší.

## K čemu to je

Podíl ztrátových firem je jednoduchý ukazatel rizika oboru. Hodí se při oceňování (obor, kde je ve ztrátě každá třetí firma, si zaslouží nižší násobek), při rozhodování banky nebo dodavatele o úvěru i při pohledu na vlastní výsledek: mírný zisk v oboru, kde je čtvrtina firem ve ztrátě, je lepší výkon než stejný zisk ve stavebnictví.

Aktuální hodnoty pro každý obor najdete v [oborových statistikách](/oborove-statistiky) v tabulce „Pracovní kapitál, přidaná hodnota a růst“.
`,
  },
  {
    slug: "jak-dlouho-cekaji-firmy-na-zaplaceni-faktur",
    title: "Jak dlouho čekají české firmy na zaplacení faktur",
    description:
      "Typická česká firma čeká na peníze od odběratelů 55 dní a svým dodavatelům platí za 61 dní. Doba obratu pohledávek a cyklus peněz podle oborů a velikosti firmy.",
    date: "2026-10-03",
    updated: "2026-10-03",
    tags: ["pohledávky", "cash flow", "pracovní kapitál"],
    charts: {
      dso: { typ: "bar", titulek: "Doba obratu pohledávek podle sekcí (dny)", jednotka: "dní",
             popisky: ["M Profesní a technické činnosti", "D Energie", "J Informační a komunikační činnosti", "A Zemědělství", "F Stavebnictví",
                       "N Administrativní činnosti", "H Doprava a skladování", "E Voda a odpady", "B Těžba", "C Zpracovatelský průmysl",
                       "P Vzdělávání", "Q Zdravotní péče", "R Kultura a rekreace", "G Obchod", "I Ubytování a pohostinství"],
             rady: [{ nazev: "Pohledávky (dny)", hodnoty: [84, 73, 72, 67, 67, 67, 63, 57, 56, 53, 48, 47, 44, 38, 24] }] },
    },
    method: "Doba obratu pohledávek = krátkodobé pohledávky / tržby × 360. Doba obratu závazků = krátkodobé závazky / tržby × 360; zahrnuje všechny krátkodobé závazky, nejen dodavatelské. Cyklus peněz = pohledávky + zásoby − závazky ve dnech. Uvedené hodnoty jsou mediány za roky 2023–2025.",
    faq: [
      ["Jak dlouho české firmy čekají na zaplacení faktur?", "Typická česká firma má v pohledávkách tržby za 55 dní (medián doby obratu krátkodobých pohledávek, roky 2023–2025)."],
      ["Ve kterém oboru se platí nejrychleji?", "V ubytování a pohostinství (24 dní) a v obchodě (38 dní). Mezi oddíly u prodejců a servisů motorových vozidel (21 dní) a v restauracích (22 dní)."],
      ["Co je cyklus peněz?", "Počet dní tržeb, které má firma vázané v provozu: doba obratu pohledávek plus doba obratu zásob minus doba obratu závazků. Medián českých firem je 23 dní."],
    ],
    body: `
**Typická česká firma má v pohledávkách tržby za 55 dní a krátkodobé závazky ve výši tržeb za 61 dní. Po započtení zásob jí v provozu „leží“ peníze za 23 dní tržeb.** Jde o mediány za roky 2023–2025 u firem s obratem nad 30 milionů korun.

## Tři čísla, která popisují pracovní kapitál

- **Doba obratu pohledávek** = krátkodobé pohledávky / tržby × 360. Jak dlouho firma čeká na peníze.
- **Doba obratu závazků** = krátkodobé závazky / tržby × 360. Jak dlouho firma sama dluží.
- **Cyklus peněz** = pohledávky + zásoby − závazky, ve dnech. Kolik dní tržeb musí firma financovat ze svého nebo z úvěru. Záporné číslo znamená, že provoz financují dodavatelé a zálohy zákazníků.

## Podle oborů

[[graf:dso]]

| Sekce CZ-NACE | Pohledávky (dny) | Závazky (dny) | Cyklus peněz (dny) |
|---|---|---|---|
| A Zemědělství, lesnictví a rybářství | 67 | 86 | 73 |
| B Těžba a dobývání | 56 | 47 | 44 |
| C Zpracovatelský průmysl | 53 | 61 | 40 |
| G Velkoobchod a maloobchod | 38 | 52 | 27 |
| M Profesní, vědecké a technické činnosti | 84 | 71 | 21 |
| F Stavebnictví | 67 | 63 | 20 |
| J Informační a komunikační činnosti | 72 | 58 | 14 |
| N Administrativní a podpůrné činnosti | 67 | 55 | 11 |
| E Voda, odpady a sanace | 57 | 58 | 11 |
| H Doprava a skladování | 63 | 59 | 7 |
| Q Zdravotní a sociální péče | 47 | 54 | −1 |
| D Výroba a rozvod energií | 73 | 110 | −7 |
| I Ubytování, stravování a pohostinství | 24 | 57 | −20 |
| R Kulturní, zábavní a rekreační činnosti | 44 | 74 | −27 |
| P Vzdělávání | 48 | 112 | −41 |

Zemědělství má nejdelší cyklus kvůli zásobám: úroda a zvířata jsou na skladě měsíce. Průmysl potřebuje financovat zhruba 40 dní tržeb. Pohostinství, kultura a vzdělávání naopak inkasují hned nebo předem a platí později, takže provoz financují dodavatelé a zákazníci.

## Kdo čeká nejkratší a nejdelší dobu

Mezi obory s aspoň 50 firmami dostávají nejrychleji zaplaceno {{45|prodejci a servisy motorových vozidel}} (21 dní), {{56|restaurace}} (22 dní) a {{47|maloobchod}} (25 dní). Nejdéle čekají {{36|vodárny}} (106 dní), {{69|právní a účetní kanceláře}} (95 dní) a {{72|výzkum a vývoj}} (91 dní).

## Větší firma čeká déle

| Velikost firmy | Pohledávky (dny) | Cyklus peněz (dny) |
|---|---|---|
| Mikro (do 9 zaměstnanců) | 48 | 11 |
| Malá (10–49) | 51 | 27 |
| Střední (50–249) | 57 | 27 |
| Velká (250+) | 64 | 20 |

Velké firmy dodávají jiným velkým firmám a exportují, kde jsou splatnosti delší. Zároveň si ale umějí vyjednat delší splatnost u svých dodavatelů, takže jejich cyklus peněz je kratší než u středních firem.

## Jak čísla použít

Když vaše firma čeká na peníze o 20 dní déle než medián oboru, při tržbách 100 milionů korun to znamená zhruba 5,5 milionu vázaných v pohledávkách navíc. To je částka, o kterou je nižší hotovost nebo vyšší úvěr.

Vlastní doby obratu a cyklus peněz proti oboru spočítá [finanční analýza](/financni-analyza) z nahrané účetní závěrky.
`,
  },
  {
    slug: "male-a-velke-firmy-kdo-ma-vyssi-marze",
    title: "Malé vs. velké firmy: kdo má vyšší marže a kdo roste",
    description:
      "Mikrofirmy mají EBITDA marži 5,4 %, střední firmy 8,4 %. Malé firmy mají nejvyšší rentabilitu aktiv a likviditu, velké rostou nejrychleji. Srovnání podle počtu zaměstnanců.",
    date: "2026-10-03",
    updated: "2026-10-03",
    tags: ["velikost firmy", "marže", "benchmark"],
    charts: {
      velikost: { typ: "bar", titulek: "EBITDA marže a ROA podle velikosti firmy (%)", jednotka: "%",
                  popisky: ["Mikro (do 9)", "Malá (10–49)", "Střední (50–249)", "Velká (250+)"],
                  rady: [{ nazev: "EBITDA marže", hodnoty: [5.4, 7.8, 8.4, 8.0] }, { nazev: "ROA", hodnoty: [7.7, 8.2, 7.3, 7.3] }] },
    },
    method: "Velikost firmy vychází z kategorie počtu zaměstnanců: mikro do 9, malá 10–49, střední 50–249, velká 250 a více. Ve statistice jsou jen firmy s obratem nad 30 milionů korun, takže skupina mikrofirem neodpovídá běžným živnostníkům. Hodnoty jsou mediány za roky 2023–2025.",
    faq: [
      ["Mají větší firmy vyšší marže?", "Jen částečně. EBITDA marže roste z 5,4 % u mikrofirem na 8,4 % u středních firem, u velkých je 8,0 %. Rentabilitu aktiv mají nejvyšší malé firmy (8,2 %)."],
      ["Které firmy rostou nejrychleji?", "Velké firmy s 250 a více zaměstnanci: tržby jim meziročně rostly o 5,6 %, mikrofirmám stagnovaly (−0,6 %)."],
    ],
    body: `
**Nejvyšší EBITDA marži mají střední firmy (8,4 %), nejnižší mikrofirmy (5,4 %). Nejlépe zhodnocují majetek malé firmy s 10–49 zaměstnanci (ROA 8,2 %) a nejrychleji rostou velké firmy (+5,6 % ročně).** Srovnávat malou firmu s mediánem celého oboru proto může zkreslovat.

## Hlavní ukazatele podle velikosti

Mediány za roky 2023–2025, firmy s obratem nad 30 milionů korun.

[[graf:velikost]]

| Ukazatel | Mikro (do 9) | Malá (10–49) | Střední (50–249) | Velká (250+) |
|---|---|---|---|---|
| Firem ve vzorku | 3 901 | 10 238 | 7 296 | 1 943 |
| EBITDA marže | 5,4 % | 7,8 % | 8,4 % | 8,0 % |
| ROA | 7,7 % | 8,2 % | 7,3 % | 7,3 % |
| Běžná likvidita | 2,08 | 2,24 | 2,05 | 1,67 |
| Celková zadluženost | 54,8 % | 45,6 % | 46,2 % | 50,3 % |
| Marže přidané hodnoty | 9,0 % | 20,7 % | 31,0 % | 31,7 % |
| Meziroční růst tržeb | −0,6 % | +1,9 % | +3,8 % | +5,6 % |
| Ztrátové firmy | 16,0 % | 12,1 % | 14,2 % | 13,4 % |

## Co z toho plyne

**Mikrofirmy s velkým obratem jsou hlavně obchodníci.** Firma s devíti lidmi a obratem přes 30 milionů typicky přeprodává zboží: marže přidané hodnoty je jen 9 %, EBITDA marže 5,4 %. Tržby jim navíc v posledních letech stagnují a ve ztrátě je každá šestá.

**Malé firmy jsou finančně nejzdravější.** Mají nejvyšší rentabilitu aktiv, nejvyšší likviditu, nejnižší zadlužení a nejméně ztrátových firem.

**Střední a velké firmy vytvářejí víc hodnoty samy.** Z každé koruny tržeb jim po zaplacení materiálu, energií a služeb zůstane 31 haléřů – třikrát víc než mikrofirmám. Většinu ale spotřebují mzdy, takže EBITDA marže je jen o něco vyšší než u malých firem.

**Velké firmy rostou nejrychleji a drží nejméně hotovosti.** Běžná likvidita 1,67 je výrazně pod ostatními skupinami: velké firmy mají přístup k úvěrům a nepotřebují takovou rezervu.

## V některých oborech je to obráceně

V dopravě a skladování marže s velikostí roste (mikro 3,0 %, velké 12,1 %): rozhoduje vlastní vozový park a sklady. V {{68|nemovitostech}} je to naopak (mikro 55 %, velké 5 %): malé firmy jen pronajímají vlastní majetek, velké poskytují služby. Ve zpracovatelském průmyslu jsou si malé, střední i velké firmy podobné (9,6 %, 8,6 % a 8,6 %).

## Jak srovnávat správně

Když porovnáváte vlastní firmu s oborem, dívejte se na firmy podobné velikosti. FinPilot to ve [finanční analýze](/financni-analyza) dělá dvěma způsoby: srovnáním s celým oborem a se skupinou firem s nejbližšími tržbami. Tabulku podle velikosti pro každý obor najdete v [oborových statistikách](/oborove-statistiky).
`,
  },
  {
    slug: "orientacni-oceneni-firmy-a-kdy-nestaci",
    title: "Orientační ocenění firmy: co z něj vyčtete a kdy už nestačí",
    description:
      "Orientační ocenění z účetních výkazů ukáže rozpětí hodnoty a slabá místa firmy. Při skutečném prodeji ale rozhodují věci, které ve výkazech nejsou. Kdy přizvat poradce.",
    date: "2026-10-03",
    updated: "2026-10-03",
    tags: ["ocenění firmy", "prodej firmy"],
    body: `
**Orientační ocenění z veřejných účetních výkazů řekne, v jakém rozpětí se hodnota firmy pravděpodobně pohybuje a co ji táhne nahoru nebo dolů. Neřekne, kolik za ni konkrétní kupec skutečně zaplatí.** Na první otázku stačí data, na druhou je potřeba proces prodeje.

## Co orientační ocenění umí

FinPilot vychází z EBITDA, velikosti firmy, stability výsledků, čistého majetku a atraktivity oboru. Výsledkem je rozpětí hodnoty a seznam faktorů, které ji ovlivnily. K tomu dostanete srovnání s oborem: kde je firma lepší než konkurence a kde zaostává.

To se hodí ve třech situacích:

- **Majitel chce vědět, kde stojí.** Bez závazků, během několika minut, jen z účetní závěrky.
- **Příprava na prodej za dva až tři roky.** Srovnání s oborem ukáže, co zlepšit dřív, než firmu uvidí kupec: marže, zadlužení, závislost výsledku na jednom roce.
- **První síto při nákupu.** Investor si rychle udělá představu o desítkách firem, než se do některé pustí podrobně.

## Co ve výkazech není

Při skutečném prodeji rozhodují věci, které účetní závěrka neukazuje:

- **Normalizace zisku.** Mzda majitele, auta a nemovitosti v majetku firmy, jednorázové výnosy a náklady. Upravená EBITDA se od účetní může výrazně lišit.
- **Závislost na majiteli a klíčových lidech.** Firma, která bez majitele nefunguje, se prodává hůř a levněji.
- **Koncentrace zákazníků.** Jeden odběratel se 40 % tržeb je riziko, které kupec ocení slevou.
- **Kdo kupuje.** Strategický kupec, který ušetří na nákladech nebo získá trh, zaplatí víc než finanční investor.
- **Struktura transakce.** Odložená část ceny, podmínky, záruky. Stejná „cena“ může znamenat velmi rozdílné peníze.

Rozdíl mezi orientačním oceněním a dosaženou cenou proto není chyba výpočtu. Je to rozdíl mezi hodnotou podle čísel a výsledkem vyjednávání.

## Kdy přizvat poradce

Jakmile o prodeji uvažujete vážně. Poradce pro prodej firem připraví firmu a podklady, osloví kupce tak, aby mezi nimi vznikla soutěž, a vede vyjednávání o ceně i podmínkách. Právě soutěž kupců má na cenu obvykle větší vliv než jakýkoli výpočet.

FinPilot vznikal ve spolupráci s poradenskou společností [Galik In](https://galikin.cz), která se prodeji a nákupu firem věnuje. Pokud řešíte prodej, nástupnictví nebo vstup investora, obraťte se na ni; praktické články o prodeji firmy najdete v jejím [rádci](https://galikin.cz/radce).

Orientační hodnotu a srovnání s oborem si předtím můžete spočítat ve [finanční analýze](/financni-analyza).
`,
  },
  {
    slug: "jak-pripravit-firmu-na-prodej-podle-cisel",
    title: "Jak připravit firmu na prodej: pět čísel, na která se kupec podívá nejdřív",
    description:
      "Kupec si firmu nejdřív porovná s oborem. Pět ukazatelů, které rozhodují o prvním dojmu, jejich typické hodnoty u českých firem a co s nimi udělat před prodejem.",
    date: "2026-10-03",
    updated: "2026-10-03",
    tags: ["prodej firmy", "benchmark", "příprava na prodej"],
    body: `
**Kupec si firmu ještě před první schůzkou porovná s oborem. Pět čísel rozhoduje o tom, jestli bude chtít jednat a s jakou cenou do jednání vstoupí: EBITDA marže, stabilita výsledků, růst tržeb, zadlužení a pracovní kapitál.** Všechna se dají zlepšit, ale trvá to dva až tři roky.

## 1. EBITDA marže proti oboru

Medián českých firem je 8,1 %, ale mezi obory se liší několikanásobně: velkoobchod a maloobchod 5,0 %, stavebnictví 7,5 %, zpracovatelský průmysl 8,8 %, voda a odpady 11,6 %. Kupce zajímá, jestli jste nad mediánem svého oboru, ne nad průměrem ekonomiky. Firma v horní čtvrtině oboru se prodává za vyšší násobek.

## 2. Stabilita výsledků

Jeden výjimečný rok cenu nezvedne. Kupec počítá s průměrem za tři roky a propad v některém z nich vnímá jako riziko. Před prodejem se vyplatí vyčistit jednorázové položky a umět každý výkyv vysvětlit.

## 3. Růst tržeb

Typická česká firma v letech 2023–2025 zvyšovala tržby o 3,1 % ročně, čtvrtina firem o víc než 14,8 %. Firma, která roste rychleji než obor, získává podíl na trhu, a to je argument pro vyšší cenu. Stagnace v rostoucím oboru je naopak otázka, na kterou musíte mít odpověď.

## 4. Zadlužení

Medián celkové zadluženosti je 48 %. Kupec od ceny firmy odečítá dluh, takže vyšší zadlužení znamená méně peněz pro prodávajícího. Zároveň zkoumá, jestli firma úvěry potřebuje k běžnému provozu, nebo jimi financovala růst.

## 5. Pracovní kapitál

Typická firma čeká na zaplacení faktur 55 dní a v provozu má vázané peníze za 23 dní tržeb. Dlouhé splatnosti a přebytečné zásoby snižují hotovost, kterou firma generuje, a kupec je promítne do ceny. Zkrácení doby inkasa je přitom jedna z mála věcí, které jdou zlepšit během měsíců.

## Co s tím

Nejdřív si udělejte obrázek: [finanční analýza](/financni-analyza) ve FinPilotu porovná těchto pět oblastí s vaším oborem i s firmami podobné velikosti a spočítá orientační hodnotu. Uvidíte, které číslo vás při prodeji bude stát nejvíc.

Samotný prodej už není otázka výpočtu, ale přípravy a vyjednávání. Tomu se věnuje poradenská společnost [Galik In](https://galikin.cz), se kterou FinPilot vznikal: připraví firmu na prodej, osloví kupce a vede transakci. Postup prodeje krok za krokem popisuje její [rádce pro majitele firem](https://galikin.cz/radce).
`,
  },
];

export const BLOG_POSTS = POSTS;
export const findPost = (slug) => POSTS.find((p) => p.slug === slug) || null;
